import { useCallback } from 'react';
import type {
  ReactElement,
} from 'react';

import styled from 'styled-components';

import { defaultRenderTab } from './defaultRenderTab';

import type {
  RenderTab,
  TabId,
  TabType,
} from './types';

export type StyledTabProps = {
  $isCurrent: boolean;
};

export const StyledTab = styled.button<StyledTabProps>(({
  $isCurrent,

  theme: {
    colors,
    fonts,
    tabs,
  },
}) => ({
  ...fonts.DEFAULT,
  display: 'inline-block',
  position: 'relative',
  verticalAlign: 'bottom',
  cursor: 'pointer',
  textDecoration: 'none',
  paddingTop: tabs.VERTICAL_PADDING,
  paddingBottom: tabs.VERTICAL_PADDING,
  paddingLeft: 0,
  paddingRight: 0,
  border: 'none',
  margin: 0,
  backgroundColor: 'transparent',
  fontWeight: $isCurrent ? tabs.ACTIVE_FONT_WEIGHT : fonts.DEFAULT.fontWeight,
  color: colors[$isCurrent ? tabs.ACTIVE_COLOR : tabs.INACTIVE_COLOR],
  outline: 'none',

  '::after': {
    content: $isCurrent ? '""' : undefined,
    display: 'block',
    position: 'absolute',
    height: tabs.ACTIVE_BORDER_WIDTH,
    left: 0,
    bottom: 0,
    width: '100%',
    backgroundColor: colors[tabs.ACTIVE_BORDER_COLOR],
  },

  ':visited': {
    color: colors[$isCurrent ? tabs.ACTIVE_COLOR : tabs.INACTIVE_COLOR],
  },

  ':hover': $isCurrent ? undefined : {
    color: colors[tabs.INACTIVE_COLOR_HOVER],

    '::after': {
      content: '""',
    },
  },

  ':active': $isCurrent ? undefined : {
    color: colors[tabs.INACTIVE_COLOR_PRESSED],

    '::after': {
      content: '""',
    },
  },
}));

export type StyledDropdownTabProps = {
  $isCurrent: boolean;
};

export const StyledDropdownTab = styled.button<StyledDropdownTabProps>(({
  $isCurrent,

  theme: {
    colors,
    fonts,
    indents,
    tabs,
  },
}) => ({
  ...fonts.DEFAULT,
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  margin: 0,
  paddingTop: 4,
  paddingBottom: 4,
  paddingLeft: indents.INDENT_2,
  paddingRight: indents.INDENT_2,
  backgroundColor: $isCurrent ? colors.ACTION_BACKGROUND : 'transparent',
  fontWeight: $isCurrent ? tabs.ACTIVE_FONT_WEIGHT : fonts.DEFAULT.fontWeight,
  color: $isCurrent ? colors.NEUTRAL_DEFAULT : colors[tabs.DROPDOWN_INACTIVE_COLOR],
  cursor: 'pointer',
  outline: 'none',
  border: 'none',
  textAlign: 'left',

  ':hover': $isCurrent ? undefined : {
    backgroundColor: colors.ACTION_BACKGROUND,
    color: colors[tabs.DROPDOWN_INACTIVE_COLOR_HOVER],
  },

  ':active': $isCurrent ? undefined : {
    backgroundColor: colors.ACTION_BACKGROUND,
    color: colors[tabs.DROPDOWN_INACTIVE_COLOR_PRESSED],
  },
}));

export type TabProps = {
  isCurrent: boolean;
  isDropdownItem: boolean;
  onClick?: () => void;
  renderTab?: RenderTab;
  setCurrentTab?: (tabId: TabId) => void;
  tab: TabType;
};

export function Tab({
  isCurrent,
  isDropdownItem,
  onClick = undefined,
  renderTab: renderTabProp = defaultRenderTab,
  setCurrentTab: setCurrentTabProp = undefined,
  tab,
}: TabProps): ReactElement {
  const setCurrentTab = useCallback(() => {
    if (setCurrentTabProp) {
      setCurrentTabProp(tab.id);
    }

    if (onClick) {
      onClick();
    }
  }, [onClick, setCurrentTabProp, tab.id]);

  const as = useCallback(({
    className = '',
  }: {
    className?: string;
  }): ReactElement => {
    const renderTab = tab.renderTab || renderTabProp;

    return renderTab({
      className,
      isCurrent,
      label: tab.title,
      onClick: setCurrentTab,
      tab,
    }) as ReactElement;
  }, [isCurrent, tab, setCurrentTab, renderTabProp, tab.title]);

  const TabComponent = isDropdownItem ? StyledDropdownTab : StyledTab;

  return (
    <TabComponent
      $isCurrent={isCurrent}
      as={as}
    />
  );
}
